/* ===================================================================
   Auth screen -- faithful rebuild of design_handoff/app/auth.html.
   Split-screen: brand panel (left) + login/signup/forgot form (right).
   Brand mark = cobalt square + white star (NO crown), via <BrandMark/>.

   Copy fix (Hard Rule #17 + no-fabricated-stats): the design's
   "Trusted by 600+ local businesses across Rhode Island" violated the
   national-market rule AND invented a number. Replaced with a
   non-localized, non-fabricated value line.

   Wiring (Hopper, 2026-06-09): now LIVE on Supabase Auth (Steve's
   confirmed approach). The old 501 stubs are gone.
     - email/password login -> supabase.auth.signInWithPassword
     - signup               -> supabase.auth.signUp
     - forgot               -> supabase.auth.resetPasswordForEmail
     - Google button        -> supabase.auth.signInWithOAuth({google})
   See src/auth/actions.ts for the wrappers and src/lib/supabase.ts for
   the browser client (publishable/anon key only -- never service-role).

   Post-login redirects:
     - new signup (just created) -> /onboarding
     - returning login           -> "/" (role-aware landing: PostAuthLanding
                                   sends operators to /agency, clients to
                                   /dashboard)
   Auth.tsx performs these via react-router navigate within this screen.
   Password RECOVERY is handled separately by the App.tsx recovery gate +
   routes/SetPassword.tsx (a recovery link must hit the set-password form
   first, never route straight into the app).

   PENDING (flagged to Charlie/Steve):
     - Google provider must be enabled in the Supabase dashboard
       (Authentication -> Providers -> Google) before the Google button
       works; it is wired regardless.
     - A new auth user has no public.clients row (auth_user_id linkage
       gap) -- /onboarding / the backend must create it, else RLS returns
       nothing. See REPORT task 4.
   =================================================================== */

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import { useSession } from '../auth/useSession';
import {
  signInWithPassword,
  signUpWithPassword,
  sendPasswordReset,
  signInWithGoogle,
  verifySignupOtp,
  resendSignupOtp,
} from '../auth/actions';
import GoogleG from '../components/GoogleG';
import { PasswordInput } from '../components/PasswordInput';
import { TurnstileBox } from '../components/TurnstileBox';
import '../styles/auth.css';

type Mode = 'login' | 'signup' | 'forgot';

function Stars({ n = 5 }: { n?: number }) {
  return (
    <span className="stars" aria-hidden="true">
      {Array.from({ length: n }).map((_, i) => (
        <svg key={i} viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 1l2 4.5 5 .4-3.8 3.3 1.1 4.8L8 11.6 3.7 14l1.1-4.8L1 5.9l5-.4z" />
        </svg>
      ))}
    </span>
  );
}

interface Note { kind: 'err' | 'ok' | 'info'; text: string; }

export function Auth({
  initialMode = 'login',
  /* Where a successful returning-user LOGIN goes.
       - undefined (default, main login at /, /start) -> "/" = client dashboard
         via PostAuthLanding. NEVER bounces an admin to /agency.
       - "/agency" (the separate admin door at /operator) -> the operator
         console. Authorization is still enforced server-side: a non-admin who
         logs in here just hits the /agency "Not available" gate. This prop only
         changes the post-login DESTINATION, never grants access.
     adminDoor toggles admin-styled copy (no signup/Google) for /operator. */
  loginLandingPath,
  adminDoor = false,
}: {
  initialMode?: Mode;
  loginLandingPath?: string;
  adminDoor?: boolean;
} = {}) {
  const navigate = useNavigate();
  // Current auth session (SessionProvider). Load-bearing for the OAuth return:
  // "Continue with Google" does a FULL-PAGE redirect back to /auth, so the
  // promise from onGoogle() is gone and nothing navigates in. Once the Supabase
  // session hydrates on this fresh mount, we forward the (now authenticated)
  // user INTO the app instead of re-showing the login form. Also forwards a
  // signed-in user who lands on /auth directly.
  const { session, loading: sessionLoading, passwordRecovery } = useSession();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note | null>(null);

  // controlled fields (shared across modes where they overlap)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [business, setBusiness] = useState('');
  // Show/hide toggle for the password field -- lets the owner reveal what they
  // typed so a fat-finger doesn't create an unloggable account (Steve 2026-07-08).
  const [showPw, setShowPw] = useState(false);

  // Cloudflare Turnstile token for the public auth forms. Empty until the
  // (env-gated) widget mints one; captchaNonce is bumped to REMOUNT the widget
  // after a failed attempt so a fresh single-use token is issued. Both are inert
  // until VITE_TURNSTILE_SITE_KEY is set at build time. See TurnstileBox.tsx.
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const resetCaptcha = () => {
    setCaptchaToken('');
    setCaptchaNonce((n) => n + 1);
  };

  // Inline email-confirmation (OTP) sub-step of signup. When an email/password
  // signup needs confirmation, we DON'T eject the owner to their inbox; we
  // transition this same card to a 6-digit code entry (see §5.2). Google signup
  // never enters this step (OAuth already verifies the email).
  const [awaitingOtp, setAwaitingOtp] = useState(false);
  const [otpCode, setOtpCode] = useState('');

  // Signup screen: Google is the dominant primary path (fastest, no password,
  // our ICP default). The email form is COLLAPSED by default behind a low-
  // emphasis "Or sign up with email" toggle so no empty input boxes pull the
  // eye on first load (Steve 2026-07-09). It expands inline on click, fully
  // functional. Google-primary + email fallback = the locked onboarding brief.
  const [emailSignupOpen, setEmailSignupOpen] = useState(false);

  /* FORWARD an authenticated user INTO the app (fixes the Google-login loop,
     2026-07-09). After the OAuth round-trip lands back on /auth, the Supabase
     PKCE ?code= is exchanged by the shared client (detectSessionInUrl) and the
     session hydrates here; we then route into the app. Guards:
       - wait for sessionLoading to settle (never decide mid-hydration),
       - NEVER forward during password recovery (App.tsx renders SetPassword
         over everything then; forwarding would skip the set-password step).
     Destination = loginLandingPath ?? '/', i.e. PostAuthLanding, which sends a
     returning user to /dashboard and a brand-new one to /welcome. */
  useEffect(() => {
    if (sessionLoading || passwordRecovery) return;
    if (session) navigate(loginLandingPath ?? '/', { replace: true });
  }, [session, sessionLoading, passwordRecovery, navigate, loginLandingPath]);

  /* Post-deletion confirmation. After a self-service account deletion the app
     signs the owner out and lands them here with ?deleted=1. Show a plain,
     honest confirmation once (there is no account to sign back into). */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (new URLSearchParams(window.location.search).has('deleted')) {
      setNote({
        kind: 'ok',
        text: 'Your account has been deleted. Thanks for trying Rhody Reviews.',
      });
    }
  }, []);

  function go(next: Mode) {
    setMode(next);
    setNote(null);
    setAwaitingOtp(false);
    setOtpCode('');
  }

  // Read a field's CURRENT value straight from the submitted <form>, falling
  // back to controlled state. Browser autofill and password managers set the
  // DOM input value WITHOUT firing React's onChange, which left email/password
  // state empty -> the submit was a silent no-op ("the Log in button does
  // nothing"). Pulling from the form at submit time captures those values too,
  // so login works however the fields were populated (typed, autofilled, or
  // pasted by a password manager). Origin: Steve "can't use the login button"
  // 2026-06-17 (Hopper).
  function readField(form: HTMLFormElement, name: string, fallback: string): string {
    const el = form.elements.namedItem(name) as HTMLInputElement | null;
    const v = el && typeof el.value === 'string' ? el.value : '';
    return (v || fallback).trim();
  }

  async function onLogin(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const form = ev.currentTarget;
    const e = readField(form, 'email', email);
    const p = (form.elements.namedItem('password') as HTMLInputElement | null)?.value || password;
    if (!e || !p) {
      setNote({ kind: 'err', text: 'Enter your email and password to log in.' });
      return;
    }
    setBusy(true);
    setNote(null);
    const res = await signInWithPassword(e, p, captchaToken || undefined);
    setBusy(false);
    if (!res.ok) {
      resetCaptcha();
      setNote({ kind: 'err', text: res.error ?? 'Could not sign in.' });
      return;
    }
    // Main login -> "/" -> client dashboard (PostAuthLanding). The admin door
    // (/operator) passes loginLandingPath="/agency" to land on the operator
    // console instead; access there is still gated server-side.
    navigate(loginLandingPath ?? '/', { replace: true });
  }

  async function onSignup(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const form = ev.currentTarget;
    const e = readField(form, 'email', email);
    const p = (form.elements.namedItem('password') as HTMLInputElement | null)?.value || password;
    const b = readField(form, 'business', business);
    if (!b || !e || !p) {
      setNote({ kind: 'err', text: 'Fill in your business name, email, and a password.' });
      return;
    }
    setBusy(true);
    setNote(null);
    const res = await signUpWithPassword(e, p, b, captchaToken || undefined);
    setBusy(false);
    if (!res.ok) {
      resetCaptcha();
      setNote({ kind: 'err', text: res.error ?? 'Could not create your account.' });
      return;
    }
    if (res.needsEmailConfirm) {
      // Email confirmation is on: no session yet. Instead of ejecting the owner
      // to their inbox (the biggest momentum killer -- see spec §5.2), keep them
      // on THIS card and collect the 6-digit code inline. The email address is
      // already captured in `email` state.
      setEmail(e);
      setAwaitingOtp(true);
      setOtpCode('');
      setNote({
        kind: 'info',
        text: `We just emailed a 6-digit code to ${e}. Enter it below to finish.`,
      });
      return;
    }
    // New user with an immediate session -> the linear onboarding chute.
    navigate('/welcome', { replace: true });
  }

  // Verify the inline 6-digit signup code. On success Supabase lands a real
  // session and we route straight to /onboarding -- no inbox context-switch.
  async function onVerifyOtp(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const code = (readField(ev.currentTarget, 'otp', otpCode) || '').replace(/\s/g, '');
    if (code.length < 6) {
      setNote({ kind: 'err', text: 'Enter the 6-digit code from your email.' });
      return;
    }
    setBusy(true);
    setNote(null);
    const res = await verifySignupOtp(email, code);
    setBusy(false);
    if (!res.ok) {
      setNote({ kind: 'err', text: res.error ?? 'That code did not work. Try again or resend.' });
      return;
    }
    // Session established -> new user -> the linear onboarding chute.
    navigate('/welcome', { replace: true });
  }

  // Resend a fresh signup code (and magic link) to the same email.
  async function onResendOtp() {
    setBusy(true);
    setNote(null);
    const res = await resendSignupOtp(email, captchaToken || undefined);
    setBusy(false);
    if (!res.ok) {
      resetCaptcha();
      setNote({ kind: 'err', text: res.error ?? 'Could not resend the code.' });
      return;
    }
    setNote({ kind: 'ok', text: `New code sent to ${email}.` });
  }

  // Back out of the OTP step to fix a mistyped email.
  function onBackToSignup() {
    setAwaitingOtp(false);
    setOtpCode('');
    setNote(null);
  }

  async function onForgot(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const e = readField(ev.currentTarget, 'email', email);
    if (!e) {
      setNote({ kind: 'err', text: 'Enter your email to get a reset link.' });
      return;
    }
    setBusy(true);
    setNote(null);
    const res = await sendPasswordReset(e, captchaToken || undefined);
    setBusy(false);
    if (!res.ok) {
      resetCaptcha();
      setNote({ kind: 'err', text: res.error ?? 'Could not send the reset link.' });
      return;
    }
    setNote({ kind: 'ok', text: 'If that email has an account, a reset link is on its way.' });
  }

  // Google sign-in / sign-up. On success the browser is redirected to
  // Google and back to /auth; detectSessionInUrl picks up the session.
  // Requires the Google provider enabled in the Supabase dashboard.
  async function onGoogle() {
    setBusy(true);
    setNote(null);
    const res = await signInWithGoogle();
    if (!res.ok) {
      setBusy(false);
      setNote({ kind: 'err', text: res.error ?? 'Google sign-in is not available right now.' });
    }
    // On success the page navigates away; no further UI update needed.
  }

  const NoteEl = note ? (
    <div className={`authnote authnote--${note.kind}`} role="status">{note.text}</div>
  ) : null;

  /* Don't flash the login form to an authenticated user (the exact moment Steve
     saw the login screen "bounce back"): once a non-recovery session exists, or
     while the OAuth PKCE ?code= is still being exchanged on return, show a brief
     "finishing sign-in" instead of the form. The effect above does the actual
     navigation the next render. */
  const oauthReturnInFlight =
    sessionLoading &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('code');
  if (!passwordRecovery && (session || oauthReturnInFlight)) {
    return (
      <div className="auth">
        <main className="auth__main">
          <form className="authcard" role="status" aria-busy="true">
            <h1>Signing you in...</h1>
            <div className="lead">One moment while we finish signing you in.</div>
          </form>
        </main>
      </div>
    );
  }

  return (
    <div className="auth">
      <aside className="auth__brand">
        <BrandMark size={30} />
        <div className="auth__pitch">
          <h2>Win Google. <span className="pop">Without lifting a finger.</span></h2>
          <p>
            Auto-ask after every job, AI-draft every reply, and turn happy
            customers into your best marketing. All from one dashboard.
          </p>
        </div>
        {/* Copy fix: no fabricated count, no RI-businesses framing. */}
        <div className="auth__proof">
          <Stars /> Every reply approved by you before it posts.
        </div>
      </aside>

      <main className="auth__main">
        {mode === 'login' && (
          <form className="authcard" onSubmit={onLogin}>
            <h1>{adminDoor ? 'Operator sign-in' : 'Welcome back'}</h1>
            <div className="lead">
              {adminDoor
                ? 'Rhody Reviews operators only. Sign in to the operator console.'
                : 'Log in to your Rhody Reviews dashboard.'}
            </div>
            {!adminDoor && (
              <>
                <button type="button" className="ssolink" onClick={onGoogle} disabled={busy}>
                  <GoogleG /> Continue with Google
                </button>
                <div className="divider">or</div>
              </>
            )}
            <div className="fld">
              <span className="lab">Email</span>
              <input
                className="inp" name="email" type="email" autoComplete="email" required
                placeholder=""
                value={email} onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="fld">
              <span className="lab">Password</span>
              <PasswordInput
                className="inp" name="password" autoComplete="current-password" required
                placeholder=""
                value={password} onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="rowbetween">
              <label className="remember">
                <input type="checkbox" defaultChecked />
                <span>Remember me</span>
              </label>
              <button type="button" className="link" onClick={() => go('forgot')}>
                Forgot password?
              </button>
            </div>
            <TurnstileBox key={`ts-login-${captchaNonce}`} onToken={setCaptchaToken} />
            <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
              {busy ? 'Logging in...' : 'Log in'}
            </button>
            {NoteEl}
            {adminDoor ? (
              <div className="switchnote">
                Not an operator?{' '}
                <button type="button" className="link" onClick={() => navigate('/auth')}>Client login</button>
              </div>
            ) : (
              <div className="switchnote">
                New to Rhody Reviews?{' '}
                <button type="button" className="link" onClick={() => go('signup')}>Create account</button>
              </div>
            )}
          </form>
        )}

        {/* ---------------------------------------------------------------
            Inline email-confirmation (OTP) step -- §5.2. Replaces the old
            "check your email, then log in" DEAD-END. The owner never leaves
            this card: they type the 6-digit code we just emailed and land
            straight in /onboarding on success.

            CONFIG DEPENDENCY (Steve sets this at deploy): the 6-digit code
            only appears in the email if the Supabase "Confirm signup" template
            includes the {{ .Token }} variable. If the template still sends a
            LINK only, this UI still degrades gracefully -- clicking that link
            from the inbox establishes the session (SessionProvider consumes it
            via detectSessionInUrl), and the "prefer the link in your email?"
            line below points the owner there.
            --------------------------------------------------------------- */}
        {mode === 'signup' && awaitingOtp && (
          <form className="authcard" onSubmit={onVerifyOtp}>
            <h1>Confirm your email</h1>
            <div className="lead">
              Enter the 6-digit code we just emailed to <strong>{email}</strong>.
              This keeps you moving -- no need to leave this page.
            </div>
            <div className="fld">
              <span className="lab">6-digit code</span>
              <input
                className="inp otpinp"
                name="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                required
                placeholder="123456"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                autoFocus
              />
            </div>
            {/* Token here feeds the "Resend code" action (a fresh signup email),
                which Supabase gates with CAPTCHA when enabled. Verify itself
                does not require a token. */}
            <TurnstileBox key={`ts-otp-${captchaNonce}`} onToken={setCaptchaToken} />
            <button type="submit" className="btn btn--primary btn--block" style={{ marginTop: 6 }} disabled={busy}>
              {busy ? 'Verifying...' : 'Verify & continue'}
            </button>
            {NoteEl}
            <div className="otphelp">
              <button type="button" className="link" onClick={onResendOtp} disabled={busy}>
                Resend code
              </button>
              <span className="otphelp__sep" aria-hidden="true">&middot;</span>
              <button type="button" className="link" onClick={onBackToSignup} disabled={busy}>
                Wrong email? Go back
              </button>
            </div>
            {/* Graceful degrade for a link-only "Confirm signup" template. */}
            <div className="otpfallback">
              Prefer the link in your email? Click the confirmation link we sent
              and you will be signed in automatically.
            </div>
          </form>
        )}

        {mode === 'signup' && !awaitingOtp && (
          <form className="authcard" onSubmit={onSignup}>
            <h1>Start free</h1>
            <div className="lead">
              14-day <b><i>free</i></b> trial. Cancel anytime.
            </div>
            {/* §5.1 -- Google is the VISUAL PRIMARY on signup: it skips the
                password AND email confirmation entirely. Rendered as the big
                primary-weight action; the email/password path sits below the
                "or" divider as the secondary route. */}
            <button
              type="button"
              className="ssolink ssolink--primary"
              onClick={onGoogle}
              disabled={busy}
            >
              <GoogleG size={20} /> Sign up with Google
            </button>
            <div className="ssohint">Fastest path. No password, nothing to confirm.</div>

            {/* Email signup is COLLAPSED by default (Steve 2026-07-09): Google is
                the clear focus. A low-emphasis toggle reveals the email form
                inline -- no empty input boxes pull the eye on first load. */}
            {!emailSignupOpen ? (
              <button
                type="button"
                className="link authswitch-email"
                onClick={() => setEmailSignupOpen(true)}
              >
                Or sign up with email
              </button>
            ) : (
              <>
                <div className="divider">or sign up with email</div>
                <div className="fld">
                  <span className="lab">Business name</span>
                  <input
                    className="inp" name="business" required placeholder=""
                    value={business} onChange={(e) => setBusiness(e.target.value)}
                  />
                </div>
                <div className="fld">
                  <span className="lab">Work email</span>
                  <input
                    className="inp" name="email" type="email" autoComplete="email" required
                    placeholder=""
                    value={email} onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="fld">
                  <span className="lab">Password</span>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="inp" name="password" type={showPw ? 'text' : 'password'}
                      autoComplete="new-password" required minLength={8}
                      placeholder=""
                      style={{ paddingRight: 60 }}
                      value={password} onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button" className="link"
                      onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13 }}
                    >
                      {showPw ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <div className="ssohint" style={{ textAlign: 'left', marginTop: 6 }}>Minimum 8 characters.</div>
                </div>
                <TurnstileBox key={`ts-signup-${captchaNonce}`} onToken={setCaptchaToken} />
                <button type="submit" className="btn btn--primary btn--block" style={{ marginTop: 8 }} disabled={busy}>
                  {busy ? 'Creating...' : 'Start free trial'}
                </button>
              </>
            )}
            {NoteEl}
            <div className="legal">
              By continuing, you agree to the Rhody Reviews{' '}
              <a href="https://rhodyreviews.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
              and{' '}
              <a href="https://rhodyreviews.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </div>
            <div className="switchnote">
              Already have an account?{' '}
              <button type="button" className="link" onClick={() => go('login')}>Log in</button>
            </div>
          </form>
        )}

        {mode === 'forgot' && (
          <form className="authcard" onSubmit={onForgot}>
            <h1>Reset password</h1>
            <div className="lead">Enter your email and we will send a reset link.</div>
            <div className="fld">
              <span className="lab">Email</span>
              <input
                className="inp" name="email" type="email" autoComplete="email" required
                placeholder=""
                value={email} onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <TurnstileBox key={`ts-forgot-${captchaNonce}`} onToken={setCaptchaToken} />
            <button type="submit" className="btn btn--primary btn--block" style={{ marginTop: 6 }} disabled={busy}>
              {busy ? 'Sending...' : 'Send reset link'}
            </button>
            {NoteEl}
            <div className="switchnote">
              <button type="button" className="link" onClick={() => go('login')}>&larr; Back to log in</button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

export default Auth;
